<template>
  <div class="sw-persona">
    <div v-if="permitirJuridica" class="sw-segmento" role="radiogroup" :aria-label="t('Tipo de persona')">
      <button type="button" role="radio" :aria-checked="persona.tipoPersona === 'FISICA'"
        :class="{ activo: persona.tipoPersona === 'FISICA' }" @click="cambiarTipo('FISICA')">{{ t('Persona física') }}</button>
      <button type="button" role="radio" :aria-checked="persona.tipoPersona === 'JURIDICA'"
        :class="{ activo: persona.tipoPersona === 'JURIDICA' }" @click="cambiarTipo('JURIDICA')">{{ t('Empresa o sociedad') }}</button>
    </div>

    <v-row dense>
      <!-- Persona física -->
      <template v-if="persona.tipoPersona === 'FISICA'">
        <v-col cols="12" sm="6">
          <v-text-field v-model="persona.nombre" :label="t('Nombre')" autocomplete="given-name"
            variant="outlined" density="comfortable" :error-messages="msgs('nombre')"
            @update:model-value="escribir('nombre')" @blur="tocar('nombre')" />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field v-model="persona.apellidos" :label="t('Apellidos')" autocomplete="family-name"
            variant="outlined" density="comfortable" :error-messages="msgs('apellidos')"
            @update:model-value="escribir('apellidos')" @blur="tocar('apellidos')" />
        </v-col>
        <v-col cols="12" sm="5">
          <v-select v-model="persona.documento.tipo" :items="tiposDocumento" item-title="title" item-value="value"
            :label="t('Tipo de documento')" variant="outlined" density="comfortable"
            @update:model-value="escribir('documento')" />
        </v-col>
        <v-col cols="12" sm="7">
          <v-text-field v-model="persona.documento.numero" :label="t('Número de documento')" autocapitalize="characters"
            spellcheck="false" variant="outlined" density="comfortable"
            :hint="ayudaDocumento(persona.documento.tipo)" :error-messages="msgs('documento')"
            @update:model-value="escribir('documento')" @blur="salirDocumento('documento', persona.documento)">
            <template #append-inner><v-icon v-if="ok('documento', persona.documento.numero)" color="success" size="20">mdi-check-circle</v-icon></template>
          </v-text-field>
        </v-col>
        <!-- Extranjero con pasaporte o documento UE/EEE en un trámite con trascendencia tributaria -->
        <v-col v-if="pideNie" cols="12" sm="7" offset-sm="5">
          <v-text-field v-model="persona.nie" :label="t('NIE (si lo tiene)')" autocapitalize="characters"
            spellcheck="false" variant="outlined" density="comfortable" persistent-hint
            :hint="t('Para esta operación la notaría necesita el NIE. Si aún no lo tiene, le indicará cómo solicitarlo.')"
            :error-messages="msgs('nie')"
            @update:model-value="escribir('nie')" @blur="salirNie">
            <template #append-inner><v-icon v-if="ok('nie', persona.nie ?? '')" color="success" size="20">mdi-check-circle</v-icon></template>
          </v-text-field>
        </v-col>
      </template>

      <!-- Persona jurídica -->
      <template v-else>
        <v-col cols="12" sm="8">
          <v-text-field v-model="persona.razonSocial" :label="t('Razón social')" autocomplete="organization"
            variant="outlined" density="comfortable" :error-messages="msgs('razonSocial')"
            @update:model-value="escribir('razonSocial')" @blur="tocar('razonSocial')" />
        </v-col>
        <v-col cols="12" sm="4">
          <v-text-field v-model="persona.documento.numero" :label="t('CIF')" autocapitalize="characters" spellcheck="false"
            variant="outlined" density="comfortable" :hint="t('Ej.: B12345674')" :error-messages="msgs('documento')"
            @update:model-value="escribir('documento')" @blur="salirDocumento('documento', persona.documento)">
            <template #append-inner><v-icon v-if="ok('documento', persona.documento.numero)" color="success" size="20">mdi-check-circle</v-icon></template>
          </v-text-field>
        </v-col>
        <v-col cols="12"><p class="sw-subtitulo">{{ t('Persona que la representa') }}</p></v-col>
        <v-col cols="12" sm="6">
          <v-text-field v-model="persona.representante.nombre" :label="t('Nombre')"
            variant="outlined" density="comfortable" :error-messages="msgs('representante.nombre')"
            @update:model-value="escribir('representante.nombre')" @blur="tocar('representante.nombre')" />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field v-model="persona.representante.apellidos" :label="t('Apellidos')"
            variant="outlined" density="comfortable" :error-messages="msgs('representante.apellidos')"
            @update:model-value="escribir('representante.apellidos')" @blur="tocar('representante.apellidos')" />
        </v-col>
        <v-col cols="12" sm="5">
          <v-select v-model="persona.representante.documento.tipo" :items="tiposDocumento" item-title="title" item-value="value"
            :label="t('Tipo de documento')" variant="outlined" density="comfortable"
            @update:model-value="escribir('representante.documento')" />
        </v-col>
        <v-col cols="12" sm="7">
          <v-text-field v-model="persona.representante.documento.numero" :label="t('Número (opcional)')" spellcheck="false"
            variant="outlined" density="comfortable" :hint="ayudaDocumento(persona.representante.documento.tipo)"
            :error-messages="msgs('representante.documento')"
            @update:model-value="escribir('representante.documento')"
            @blur="salirDocumento('representante.documento', persona.representante.documento)">
            <template #append-inner><v-icon v-if="ok('representante.documento', persona.representante.documento.numero)" color="success" size="20">mdi-check-circle</v-icon></template>
          </v-text-field>
        </v-col>
      </template>

      <!-- Contacto -->
      <template v-if="mostrarContacto">
        <v-col cols="12" sm="6">
          <v-text-field v-model="persona.telefono" :label="contactoObligatorio ? t('Teléfono') : t('Teléfono (opcional)')"
            type="tel" inputmode="tel" autocomplete="tel" variant="outlined" density="comfortable"
            :hint="t('Si no es español, con prefijo: +44…')" :error-messages="msgs('telefono')"
            @update:model-value="escribir('telefono')" @blur="tocar('telefono')">
            <template #append-inner><v-icon v-if="ok('telefono', persona.telefono)" color="success" size="20">mdi-check-circle</v-icon></template>
          </v-text-field>
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field v-model="persona.email" :label="contactoObligatorio ? t('Email') : t('Email (opcional)')"
            type="email" inputmode="email" autocomplete="email" spellcheck="false" variant="outlined" density="comfortable"
            :error-messages="msgs('email')" :messages="sugerenciaEmailTexto ? [sugerenciaEmailTexto] : []"
            :class="{ 'sw-sugerencia': sugerenciaEmailTexto }"
            @update:model-value="escribir('email')" @blur="salirEmail">
            <template #append-inner><v-icon v-if="ok('email', persona.email)" color="success" size="20">mdi-check-circle</v-icon></template>
          </v-text-field>
          <button v-if="dominioSugerido" type="button" class="sw-editar sw-corregir" @click="aplicarSugerencia">
            {{ t('Usar {email}', { email: emailCorregido }) }}
          </button>
        </v-col>
      </template>

      <!-- Papel en la operación -->
      <v-col v-if="roles.length > 1" cols="12" sm="6">
        <v-select v-model="persona.rol" :items="roles" item-title="nombre" item-value="codigo"
          :label="t(etiquetaRol)" variant="outlined" density="comfortable" :error-messages="msgs('rol')"
          @update:model-value="escribir('rol')" />
      </v-col>
      <v-col v-if="persona.tipoPersona === 'FISICA' && mostrarConyuge" cols="12" :sm="roles.length > 1 ? 6 : 12"
        class="d-flex align-center">
        <v-switch v-model="persona.intervieneConyuge" :label="t('También interviene su cónyuge')"
          color="primary" hide-details inset />
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import { t } from '../../composables/solicitudTextos'
import { computed, reactive } from 'vue'
import type { DocumentoIdentidad, Interviniente, RolCatalogo, TipoPersona } from '../../types/solicitudWeb'
import {
  TIPOS_DOCUMENTO, detectarTipoDocumento, errorDocumento, errorEmail, errorTelefono, sugerenciaEmail,
  necesitaNie, normalizarDocumento, useSolicitudWeb, validarDocumento
} from '../../composables/useSolicitudWeb'

const props = withDefaults(defineProps<{
  persona: Interviniente
  prefijo: string
  roles?: RolCatalogo[]
  etiquetaRol?: string
  permitirJuridica?: boolean
  mostrarContacto?: boolean
  contactoObligatorio?: boolean
  mostrarConyuge?: boolean
}>(), {
  roles: () => [],
  etiquetaRol: 'Papel en la operación',
  permitirJuridica: true,
  mostrarContacto: true,
  contactoObligatorio: false,
  mostrarConyuge: true
})

const { errores, limpiarError, tipo } = useSolicitudWeb()

/** Se pide el NIE aparte a los extranjeros que se identifican con pasaporte o documento UE/EEE. */
const pideNie = computed(() => necesitaNie(props.persona, tipo.value))

function salirNie() {
  if (props.persona.nie === undefined) props.persona.nie = ''
  props.persona.nie = normalizarDocumento(props.persona.nie)
  tocar('nie')
}

/** Campos que el usuario ya ha rellenado y abandonado: a partir de ahí se comprueban mientras escribe. */
const tocados = reactive<Record<string, boolean>>({})

function tocar(campo: string) {
  tocados[campo] = true
}

function escribir(campo: string) {
  limpiarError(`${props.prefijo}.${campo}`)
}

/** Comprobación en vivo de cada campo (null = correcto o vacío). */
function vivo(campo: string): string | null {
  const p = props.persona
  switch (campo) {
    case 'documento': return errorDocumento(p.documento.tipo, p.documento.numero)
    case 'representante.documento': return errorDocumento(p.representante.documento.tipo, p.representante.documento.numero)
    case 'nie': return errorDocumento('NIE', p.nie ?? '')
    case 'telefono': return errorTelefono(p.telefono)
    case 'email': return errorEmail(p.email)
    default: return null
  }
}

function msgs(campo: string): string[] {
  const enviado = errores.value[`${props.prefijo}.${campo}`]
  if (enviado) return [enviado]
  if (tocados[campo]) {
    const m = vivo(campo)
    if (m) return [m]
  }
  return []
}

function ok(campo: string, valor: string): boolean {
  return !!tocados[campo] && !!(valor && valor.trim()) && !vivo(campo) && !errores.value[`${props.prefijo}.${campo}`]
}

const tiposDocumento = computed(() => TIPOS_DOCUMENTO.map((x) => ({ ...x, title: t(x.title) })))

function ayudaDocumento(tipo: string) {
  const d = TIPOS_DOCUMENTO.find((x) => x.value === tipo)
  return d ? t(d.ayuda) : ''
}

/** Al salir del número: se normaliza y, si es claramente un DNI o NIE, se corrige el tipo. */
function salirDocumento(campo: string, doc: DocumentoIdentidad) {
  doc.numero = normalizarDocumento(doc.numero)
  if (doc.tipo !== 'CIF') {
    const detectado = detectarTipoDocumento(doc.numero)
    if (detectado && detectado !== doc.tipo && validarDocumento(detectado, doc.numero)) doc.tipo = detectado
  }
  tocar(campo)
}

// --- Sugerencia de email (gmial.com → gmail.com) ---------------------------
const dominioSugerido = computed(() => (tocados.email ? sugerenciaEmail(props.persona.email) : null))
const sugerenciaEmailTexto = computed(() =>
  dominioSugerido.value ? t('¿Quería decir @{dominio}?', { dominio: dominioSugerido.value }) : null)
const emailCorregido = computed(() =>
  dominioSugerido.value ? props.persona.email.trim().split('@')[0] + '@' + dominioSugerido.value : '')

function salirEmail() {
  props.persona.email = props.persona.email.trim()
  tocar('email')
}

function aplicarSugerencia() {
  props.persona.email = emailCorregido.value
}

function cambiarTipo(t: TipoPersona) {
  props.persona.tipoPersona = t
  props.persona.documento.tipo = t === 'JURIDICA' ? 'CIF' : 'DNI'
  if (t === 'JURIDICA') props.persona.intervieneConyuge = false
  escribir('documento')
}
</script>
